/*
 * TikTok: Login Kit for Web, and the Content Posting API in its Direct Post mode.
 *
 * Read from the official documentation (developers.tiktok.com/doc/...):
 *   login-kit-web                                   authorize URL, client_key, comma scopes, no PKCE for web
 *   oauth-user-access-token-management              token, refresh and revoke (form bodies, client_key/client_secret)
 *   tiktok-api-v2-get-user-info                     GET /v2/user/info/?fields=...  (data.user.*)
 *   content-posting-api-get-started                 video.publish scope, the audit restriction
 *   content-posting-api-reference-direct-post       POST /v2/post/publish/video/init/ (post_info, source_info)
 *   content-posting-api-media-transfer-guide        PUT with Content-Range, chunk rules
 *   content-posting-api-reference-get-video-status  POST /v2/post/publish/status/fetch/
 *   tiktok-api-v2-video-list                        POST /v2/video/list/ (scope video.list)
 *   tiktok-api-v2-video-object                      create_time is UTC epoch seconds
 *
 * Chunk rules (media-transfer guide): a file under 5 MB goes whole in one
 * request; otherwise each chunk is 5 MB to 64 MB, the last may run to 128 MB to
 * take the trailing bytes, total_chunk_count is the size divided by the chunk
 * size ROUNDED DOWN, at most 1000 chunks, sent in order. The engine's
 * `lastChunkAbsorbsRemainder` computes exactly that count. 10 MiB is used here:
 * a 4 GB file (TikTok's ceiling) is then about 400 chunks.
 *
 * Not supported, on purpose:
 *   - Photos. Direct Post for photos accepts only PULL_FROM_URL (TikTok pulls
 *     from a verified domain), which this engine cannot do. Video only.
 *   - Editing and deleting. The public API has no endpoint for either.
 *   - The creator_info query (POST /v2/post/publish/creator_info/query/). TikTok
 *     asks an app to call it before each post and to offer the creator only the
 *     privacy levels it returns. The engine has no step for that, so the privacy
 *     level is one app setting instead, and the creator's real options are not
 *     checked here. A level the creator does not have is refused by TikTok
 *     (privacy_level_option_mismatch) and shown as the error.
 *   - Other post options (disable_duet, comments, brand content) are sent as
 *     false/default.
 *
 * Uncertain, to prove with a live post:
 *   - An unaudited app can post only privately (SELF_ONLY); TikTok answers
 *     unaudited_client_can_only_post_to_private_accounts for anything else.
 *     Content that must be public needs the audit first. The app setting has no
 *     default in this schema, so it must be filled in: SELF_ONLY until audited.
 *   - publicaly_available_post_id (TikTok's own spelling) is filled in only for
 *     a post that is public AND has passed moderation. When it is absent, which
 *     is always the case for SELF_ONLY, the stored post id is the publish_id.
 *     That id is not a video id, so a post's link only resolves once a real
 *     video id is known (see `permalink`).
 *   - The upload address host (documented only as <= 256 characters). Every
 *     tiktokapis.com and tiktok.com subdomain is allowed.
 *   - Listing needs video.list, which TikTok approves separately; the
 *     `listVideos` setting turns it on and adds the scope to the login.
 *   - Status polling is rate-limited to 30 a minute per token; every 5 seconds
 *     is 12.
 */
const TIKTOK_API = 'https://open.tiktokapis.com';
export const TIKTOK_PRESET = {
    key: 'TIKTOK',
    name: 'TikTok',
    verifiedLive: false,
    contract: {
        version: 1,
        allowedHosts: ['open.tiktokapis.com', '*.tiktokapis.com', 'www.tiktok.com', '*.tiktok.com'],
        auth: {
            type: 'oauth2',
            authorizeUrl: 'https://www.tiktok.com/v2/auth/authorize/',
            tokenUrl: `${TIKTOK_API}/v2/oauth/token/`,
            revokeUrl: `${TIKTOK_API}/v2/oauth/revoke/`,
            scopes: ['user.info.basic', 'video.publish'],
            scopesWhen: { listVideos: ['video.list'] },
            scopeSeparator: ',',
            // Login Kit for Web is a plain authorization-code flow; code_verifier is for mobile and desktop.
            pkce: false,
            clientIdParam: 'client_key',
            clientSecretIn: 'body',
            refresh: true,
        },
        tokenPlacement: { in: 'bearer' },
        settings: [
            {
                name: 'privacyLevel',
                label: 'Who can see posts: SELF_ONLY until TikTok has audited the app (then PUBLIC_TO_EVERYONE, MUTUAL_FOLLOW_FRIENDS or FOLLOWER_OF_CREATOR)',
                type: 'string',
                default: 'SELF_ONLY',
                options: ['SELF_ONLY', 'PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'FOLLOWER_OF_CREATOR'],
            },
            {
                name: 'listVideos',
                label: 'Read back the account\'s videos (needs the video.list scope approved)',
                type: 'boolean',
            },
        ],
        identity: {
            request: { method: 'GET', url: `${TIKTOK_API}/v2/user/info/?fields=open_id,display_name` },
            idPath: 'data.user.open_id',
            namePath: 'data.user.display_name',
            accountId: '{{entry.id}}',
            kind: 'PERSON',
        },
        publish: {
            kinds: ['VIDEO'],
            // Unused for a video whose upload is the post, but the contract always carries one.
            request: { method: 'POST', url: `${TIKTOK_API}/v2/post/publish/video/init/` },
            mediaIsPost: ['VIDEO'],
            resultId: { from: 'body', path: 'data.publish_id' },
            // The caption: up to 2200 UTF-16 runes.
            maxTextLength: 2200,
        },
        media: {
            video: {
                type: 'chunked',
                init: {
                    method: 'POST',
                    url: `${TIKTOK_API}/v2/post/publish/video/init/`,
                    json: {
                        post_info: {
                            title: '{{text}}',
                            privacy_level: '{{setting.privacyLevel}}',
                            disable_duet: false,
                            disable_comment: false,
                            disable_stitch: false,
                        },
                        source_info: {
                            source: 'FILE_UPLOAD',
                            video_size: '{{fileSize}}',
                            chunk_size: '{{chunkSize}}',
                            total_chunk_count: '{{chunkCount}}',
                        },
                    },
                },
                mediaIdPath: 'data.publish_id',
                uploadUrlPath: 'data.upload_url',
                chunkSize: 10 * 1024 * 1024,
                lastChunkAbsorbsRemainder: true,
                append: {
                    mode: 'put',
                    // The upload address carries its own authority: no token on it.
                    withAuth: false,
                    headers: { 'Content-Range': 'bytes {{rangeStart}}-{{rangeEnd}}/{{fileSize}}' },
                },
                poll: {
                    request: {
                        method: 'POST',
                        url: `${TIKTOK_API}/v2/post/publish/status/fetch/`,
                        json: { publish_id: '{{mediaId}}' },
                    },
                    statePath: 'data.status',
                    doneValues: ['PUBLISH_COMPLETE'],
                    failedValues: ['FAILED'],
                    errorPath: 'data.fail_reason',
                    // Present only for a public, moderated post; otherwise the publish_id stays the post id.
                    resultIdPath: 'data.publicaly_available_post_id[0]',
                    intervalMs: 5_000,
                    maxWaitMs: 10 * 60_000,
                },
            },
        },
        posts: {
            // No edit and no delete: the public API has neither.
            list: {
                request: {
                    method: 'POST',
                    url: `${TIKTOK_API}/v2/video/list/?fields=id,title,create_time`,
                    // TikTok's page limit is 20; a larger number is refused.
                    json: { max_count: 20 },
                },
                listPath: 'data.videos',
                idPath: 'id',
                textPath: 'title',
                createdPath: 'create_time',
                createdFormat: 'epoch-s',
                whenSetting: 'listVideos',
            },
        },
        /*
         * A video's address is https://www.tiktok.com/@<username>/video/<id>. The
         * account here is the open_id, which is not the username, and reading the
         * username needs another scope (user.info.profile). TikTok is understood to
         * resolve the page when the username is left empty (it redirects to the
         * owner's) — not documented, so verify live. For a post still known only by
         * its publish_id this link does not resolve.
         */
        permalink: 'https://www.tiktok.com/@/video/{{postId}}',
        errorMessagePaths: ['error.message', 'error_description', 'message'],
    },
};
//# sourceMappingURL=tiktok.js.map